import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEnvelope, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import api from "../../api/axiosClient";
import adminAvatar from "../../assets/admin.png";
import kaakaziniLogo from "../../assets/kazini.png";

export default function AdminLoginPage() {
  const [email, setEmail]             = useState("");
  const [password, setPassword]       = useState("");
  const [error, setError]             = useState("");
  const [success, setSuccess]         = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading]         = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(""); setSuccess(""); setLoading(true);
    try {
      await api.post("/admin-login/", { email, password });
      setSuccess("Access granted. Redirecting to command centre…");
      setTimeout(() => navigate("/admin-dashboard"), 1500);
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) setError("Invalid credentials. Access denied.");
      else if (status === 400) setError("Bad request. Check your input.");
      else setError("Network error. Check your connection.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="al-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Raleway:wght@400;500;600;700;800&display=swap');

        :root {
          --yellow: #fff000;
          --black:  #000000;
          --white:  #ffffff;
          --border: rgba(0,0,0,.1);
          --muted:  #6b6b6b;
        }

        * { box-sizing: border-box; margin: 0; padding: 0; }
        html, body, #root { margin: 0 !important; padding: 0 !important; }

        .al-root {
          min-height: 100vh;
          display: flex;
          background: var(--white);
          font-family: 'Raleway', sans-serif;
          color: var(--black);
          overflow: hidden;
          position: relative;
        }

        /* ── TOP-LEFT LOGO ── */
        .al-logo-fixed {
          position: absolute;
          top: 2rem; left: 2.5rem;
          z-index: 10;
          display: flex; align-items: center; gap: 10px;
        }
        .al-logo-fixed img { height: 34px; width: auto; }
        .al-logo-fixed span {
          font-size: 1.1rem; font-weight: 800; letter-spacing: .02em;
        }

        /* ── LEFT PANEL — pure white, no grid squares ── */
        .al-left {
          flex: 1.1;
          position: relative;
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: 3.5rem;
          padding-top: 6.5rem;
          overflow: hidden;
          background: var(--white);
          border-right: 1px solid var(--border);
        }
        .al-left::before {
          content: '';
          position: absolute;
          inset: 0;
          background:
            radial-gradient(ellipse 80% 60% at 20% 80%, rgba(255,240,0,.18) 0%, transparent 60%),
            radial-gradient(ellipse 60% 50% at 80% 10%, rgba(255,240,0,.1) 0%, transparent 55%);
          pointer-events: none;
        }
        .al-brand { position: relative; z-index: 1; }

        .al-headline {
          font-family: 'Raleway', sans-serif;
          font-size: clamp(2rem, 3.5vw, 2.9rem);
          font-weight: 800;
          line-height: 1.12;
          margin-bottom: 1.25rem;
          letter-spacing: -.5px;
          color: var(--black);
        }
        .al-headline .hl-yellow {
          color: var(--black);
          background: var(--yellow);
          padding: 0 6px;
        }
        .al-sub {
          color: var(--muted);
          font-size: .95rem;
          line-height: 1.75;
          max-width: 420px;
          margin-bottom: 3rem;
        }
        .al-features { display: flex; flex-direction: column; gap: 1.5rem; }
        .al-feat {
          display: flex; align-items: flex-start; gap: 14px;
        }
        .al-feat-icon {
          font-size: 1.3rem;
          line-height: 1.4;
          color: var(--black);
          flex-shrink: 0;
        }
        .al-feat-text { font-size: .88rem; color: #555; font-weight: 500; }
        .al-feat-text strong { color: var(--black); display: block; margin-bottom: 2px; font-weight: 700; }
        .al-left-footer {
          position: relative; z-index: 1;
          font-size: .75rem; color: var(--muted);
          display: flex; align-items: center; gap: 6px;
          margin-top: 2.5rem;
        }
        .al-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--black); }

        /* ── RIGHT PANEL (sign-in) ── */
        .al-right {
          flex: 0 0 480px;
          display: flex; align-items: center; justify-content: center;
          padding: 2.5rem;
          background: var(--white);
          position: relative;
        }
        .al-form-wrap { width: 100%; max-width: 360px; position: relative; z-index: 1; }

        .al-avatar-wrap { text-align: center; margin-bottom: 2rem; }
        .al-avatar {
          width: 88px; height: 88px;
          border-radius: 50%;
          object-fit: cover;
          border: 3px solid var(--yellow);
          box-shadow: 0 0 0 6px rgba(255,240,0,.15);
        }

        .al-form-title {
          text-align: center;
          font-family: 'Raleway', sans-serif;
          font-size: 1.75rem; font-weight: 800;
          letter-spacing: -.5px;
          margin-bottom: 4px;
          color: var(--black);
        }
        .al-form-sub {
          text-align: center;
          color: var(--muted); font-size: .88rem;
          margin-bottom: 2rem;
        }
        .al-alert {
          padding: .75rem 1rem;
          border-radius: 10px;
          font-size: .84rem; font-weight: 500;
          margin-bottom: 1.25rem;
          display: flex; align-items: center; gap: 8px;
        }
        .al-alert.err { background: rgba(239,68,68,.08); border: 1px solid rgba(239,68,68,.3); color: #dc2626; }
        .al-alert.ok  { background: rgba(255,240,0,.2);  border: 1px solid rgba(0,0,0,.15);   color: #6b6100; }

        .al-label {
          display: block;
          font-size: .78rem; font-weight: 600;
          color: #555; letter-spacing: .05em; text-transform: uppercase;
          margin-bottom: 8px;
        }
        .al-input-wrap { position: relative; margin-bottom: 1.25rem; }
        .al-icon {
          position: absolute; left: 14px; top: 50%;
          transform: translateY(-50%);
          color: var(--muted); font-size: .9rem; pointer-events: none;
        }
        .al-input {
          width: 100%;
          padding: .875rem 1rem .875rem 2.75rem;
          background: var(--white);
          border: 1.5px solid var(--border);
          border-radius: 10px;
          color: var(--black); font-size: .95rem;
          font-family: 'Raleway', sans-serif;
          transition: border-color .2s, box-shadow .2s;
          outline: none;
        }
        .al-input:focus {
          border-color: var(--yellow);
          box-shadow: 0 0 0 3px rgba(255,240,0,.25);
        }
        .al-input::placeholder { color: #aaa; }
        .al-eye {
          position: absolute; right: 12px; top: 50%;
          transform: translateY(-50%);
          background: none; border: none;
          color: var(--muted); cursor: pointer;
          padding: 4px; line-height: 1;
          transition: color .15s;
        }
        .al-eye:hover { color: var(--black); }

        .al-submit {
          width: 100%;
          padding: 1rem;
          background: var(--yellow);
          color: var(--black);
          border: 1.5px solid var(--black);
          border-radius: 10px;
          font-family: 'Raleway', sans-serif;
          font-size: .95rem; font-weight: 700;
          cursor: pointer;
          transition: all .25s;
          display: flex; align-items: center; justify-content: center; gap: 8px;
          margin-top: .5rem;
        }
        .al-submit:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,.15);
        }
        .al-submit:disabled { opacity: .5; cursor: not-allowed; transform: none; }

        .al-footer-note { text-align: center; font-size: .75rem; color: #aaa; margin-top: 1.5rem; }

        .al-spin {
          width: 16px; height: 16px;
          border: 2px solid rgba(0,0,0,.2);
          border-top-color: var(--black);
          border-radius: 50%;
          animation: alspin .6s linear infinite;
        }
        @keyframes alspin { to { transform: rotate(360deg); } }

        @media (max-width: 960px) { .al-left { display: none; } }
        @media (max-width: 520px) {
          .al-right { flex: 1; padding: 1.5rem; }
          .al-form-wrap { max-width: 100%; }
          .al-logo-fixed { top: 1.25rem; left: 1.5rem; }
        }
      `}</style>

      <div className="al-logo-fixed">
        <img src={kaakaziniLogo} alt="Kaakazini" />
        <span>Kaakazini</span>
      </div>

      <div className="al-left">
        <div className="al-brand">
          <h1 className="al-headline">
            Manage. Approve.<br/>
            <span className="hl-yellow">Deploy</span> the best<br/>
            craftsmen.
          </h1>
          <p className="al-sub">
            The central hub for approving craftsmen, tracking jobs, managing payments, and keeping Kenya's skilled workforce running at full power.
          </p>
          <div className="al-features">
            {[
              { icon: '✦', title: 'Craftsman Approvals', desc: 'Review profiles and approve verified professionals instantly' },
              { icon: '◈', title: 'Job Management',       desc: 'Assign, track and monitor all service requests in real time' },
              { icon: '◎', title: 'MPesa Payments',       desc: 'Process craftsman payouts directly via MPesa integration' },
            ].map(({ icon, title, desc }) => (
              <div className="al-feat" key={title}>
                <div className="al-feat-icon">{icon}</div>
                <div className="al-feat-text">
                  <strong>{title}</strong>
                  {desc}
                </div>
              </div>
            ))}
          </div>
          <div className="al-left-footer">
            <span className="al-dot"/>
            © {new Date().getFullYear()} KaaKazini · Admin Panel
          </div>
        </div>
      </div>

      <div className="al-right">
        <div className="al-form-wrap">
          <div className="al-avatar-wrap">
            <img src={adminAvatar} alt="Admin" className="al-avatar"
              onError={e => { e.target.style.display = 'none'; }}/>
          </div>
          <h2 className="al-form-title">Welcome back</h2>
          <p className="al-form-sub">Sign in to your admin account</p>

          {error   && <div className="al-alert err">⚠ {error}</div>}
          {success && <div className="al-alert ok">✓ {success}</div>}

          <form onSubmit={handleLogin}>
            <label className="al-label">Email address</label>
            <div className="al-input-wrap">
              <FaEnvelope className="al-icon"/>
              <input className="al-input" type="email" placeholder="admin@kaakazini.com"
                value={email} onChange={e => setEmail(e.target.value)} required autoFocus/>
            </div>

            <label className="al-label">Password</label>
            <div className="al-input-wrap">
              <FaLock className="al-icon"/>
              <input className="al-input" type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={password} onChange={e => setPassword(e.target.value)} required/>
              <button type="button" className="al-eye" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <FaEyeSlash size={16}/> : <FaEye size={16}/>}
              </button>
            </div>

            <button className="al-submit" type="submit" disabled={loading}>
              {loading ? <><div className="al-spin"/> Verifying…</> : '→ Sign In to Admin'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}